/**
 * Usage & Billing read contract (AIT-255…259).
 *
 * Этот модуль держит в одном месте те места контракта, где поведение бэкенда
 * не очевидно из подписи ручки и где наивная реализация на фронте даёт молча
 * неверные числа. Всё остальное — обычные вызовы в `services/api`.
 *
 * Что именно здесь закрыто:
 *  - `from` исключающая, `to` включающая (`created_at > from AND created_at <= to`);
 *  - `limit` вне `1..100` не отдаёт 400, а тихо становится 20;
 *  - `operation` нормализован только у новых строк леджера, старые остались
 *    с прежним написанием — группировка обязана считать их одним значением;
 *  - отчёт сверки приходит в трёх разных формах без общего поля-дискриминатора;
 *  - `token_balance` теперь может быть отрицательным (разрешён перерасход).
 */

/** Бэкенд молча подменяет значение вне диапазона на это. */
export const USAGE_LIMIT_FALLBACK = 20;
export const USAGE_LIMIT_MIN = 1;
export const USAGE_LIMIT_MAX = 100;

/**
 * Приводит размер страницы к тому, что бэкенд реально применит.
 *
 * Ручка не возвращает 400 на плохой `limit`, а подставляет 20. Если отправить
 * 250 и рисовать пагинацию из расчёта 250, страницы разъедутся с ответом —
 * поэтому клэмпим до отправки и считаем страницы по тому же числу.
 */
export function clampUsageLimit(value: number | null | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return USAGE_LIMIT_FALLBACK;
  const int = Math.trunc(value);
  if (int < USAGE_LIMIT_MIN || int > USAGE_LIMIT_MAX) return USAGE_LIMIT_FALLBACK;
  return int;
}

export interface UsageRangeParams {
  from?: string;
  to?: string;
}

/**
 * Переводит выбранные в календаре даты (`YYYY-MM-DD`) в границы запроса.
 *
 * Пользователь, выбравший «с 1 по 5 сентября», ожидает оба дня целиком.
 * Контракт же сравнивает `created_at > from` и `created_at <= to`, поэтому:
 *  - нижняя граница сдвигается на миллисекунду назад, иначе запись, созданная
 *    ровно в 00:00:00.000 первого дня, не попадёт в выборку;
 *  - верхняя граница поднимается до конца дня, иначе из выборки выпадает весь
 *    последний день — он обрезается полуночью.
 */
export function usageRangeParams(fromYmd?: string | null, toYmd?: string | null): UsageRangeParams {
  const params: UsageRangeParams = {};
  const from = parseYmd(fromYmd);
  if (from) {
    params.from = new Date(from.getTime() - 1).toISOString();
  }
  const to = parseYmd(toYmd);
  if (to) {
    to.setUTCHours(23, 59, 59, 999);
    params.to = to.toISOString();
  }
  return params;
}

/** `true`, если диапазон задан задом наперёд — ловим до запроса, а не по 400. */
export function isReversedRange(fromYmd?: string | null, toYmd?: string | null): boolean {
  const from = parseYmd(fromYmd);
  const to = parseYmd(toYmd);
  if (!from || !to) return false;
  return from.getTime() > to.getTime();
}

function parseYmd(value?: string | null): Date | null {
  const raw = (value ?? '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const parsed = new Date(`${raw}T00:00:00.000Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Единое написание операции.
 *
 * Словарь операций канонизировали, но старые строки леджера не мигрировали:
 * в базе рядом лежат `dataset_conversion` и `"dataset conversion"`. Для
 * пользователя это одна и та же операция, и в разбивке она обязана быть одной
 * строкой, иначе сумма «по операциям» визуально не сойдётся с итогом.
 */
export function canonicalOperation(value: string | null | undefined): string {
  const raw = (value ?? '').trim().toLowerCase();
  if (!raw) return 'unknown';
  return raw.replace(/[\s-]+/g, '_').replace(/_+/g, '_');
}

export function formatOperationLabel(value: string | null | undefined): string {
  const key = canonicalOperation(value);
  if (key === 'unknown') return 'Unknown';
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export interface OperationRow {
  operation: string;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  cached_tokens: number;
  total_cost?: number;
  records_count: number;
}

/**
 * Схлопывает разбивку по операциям в канонические ключи.
 *
 * Складываем, а не выбираем одну из строк: обе половины — настоящий расход,
 * просто записанный в разное время и с разным написанием.
 */
export function mergeOperationRows(rows: OperationRow[] | null | undefined): OperationRow[] {
  if (!Array.isArray(rows)) return [];
  const byKey = new Map<string, OperationRow>();
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const key = canonicalOperation(row.operation);
    const prev = byKey.get(key);
    if (!prev) {
      byKey.set(key, { ...row, operation: key });
      continue;
    }
    byKey.set(key, {
      operation: key,
      prompt_tokens: num(prev.prompt_tokens) + num(row.prompt_tokens),
      completion_tokens: num(prev.completion_tokens) + num(row.completion_tokens),
      total_tokens: num(prev.total_tokens) + num(row.total_tokens),
      cached_tokens: num(prev.cached_tokens) + num(row.cached_tokens),
      total_cost: sumOptional(prev.total_cost, row.total_cost),
      records_count: num(prev.records_count) + num(row.records_count),
    });
  }
  return Array.from(byKey.values()).sort((a, b) => b.total_tokens - a.total_tokens);
}

function num(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/** Стоимость необязательна: если её нет ни у одной половины, не выдумываем ноль. */
function sumOptional(a: number | undefined, b: number | undefined): number | undefined {
  if (typeof a !== 'number' && typeof b !== 'number') return undefined;
  return num(a) + num(b);
}

/* ─── Сверка ML-леджера с базой ───────────────────────────────────────────── */

export interface ReconciliationDiscrepancy {
  request_id: string;
  ml_total_tokens: number;
  db_total_tokens: number;
  discrepancy_pct: number;
}

export type ReconciliationView =
  | { state: 'unavailable' }
  | { state: 'not_run_yet' }
  | {
      state: 'report';
      generatedAt: string | null;
      from: string | null;
      to: string | null;
      runsChecked: number;
      discrepancies: ReconciliationDiscrepancy[];
      error: string;
    };

/**
 * Приводит три возможные формы ответа сверки к одному разбору.
 *
 * Общего поля-дискриминатора в ответе нет: `{"status":"unavailable"}`,
 * `{"status":"not_run_yet"}` и сам отчёт — три разные фигуры. Неизвестную форму
 * трактуем как «сверка недоступна», а не роняем экран: отчёт живёт в памяти
 * процесса, и после рестарта бэкенда сюда легко прилетит что угодно.
 */
export function parseReconciliation(raw: unknown): ReconciliationView {
  if (!raw || typeof raw !== 'object') return { state: 'unavailable' };
  const data = raw as Record<string, unknown>;
  const status = typeof data.status === 'string' ? data.status : '';
  if (status === 'unavailable') return { state: 'unavailable' };
  if (status === 'not_run_yet') return { state: 'not_run_yet' };
  if (!('generated_at' in data) && !('discrepancies' in data)) return { state: 'unavailable' };

  return {
    state: 'report',
    generatedAt: str(data.generated_at),
    from: str(data.from),
    to: str(data.to),
    runsChecked: num(data.ml_runs_checked),
    discrepancies: Array.isArray(data.discrepancies)
      ? data.discrepancies.filter(isDiscrepancy).map((d) => ({
          request_id: String(d.request_id),
          ml_total_tokens: num(d.ml_total_tokens),
          db_total_tokens: num(d.db_total_tokens),
          discrepancy_pct: num(d.discrepancy_pct),
        }))
      : [],
    error: typeof data.error === 'string' ? data.error : '',
  };
}

function isDiscrepancy(value: unknown): value is Record<string, unknown> & { request_id: unknown } {
  return !!value && typeof value === 'object' && 'request_id' in (value as Record<string, unknown>);
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

/* ─── Баланс ──────────────────────────────────────────────────────────────── */

export type BalanceTone = 'overdraft' | 'empty' | 'positive';

/**
 * Перерасход больше не клэмпится в ноль, поэтому отрицательный баланс —
 * штатное состояние счёта, а не битые данные. Отличаем его от нуля явно:
 * «0» означает «кончились», «−1 200» — «ушёл в минус», и это разные разговоры
 * с пользователем.
 */
export function balanceTone(balance: number | null | undefined): BalanceTone {
  if (typeof balance !== 'number' || !Number.isFinite(balance)) return 'empty';
  if (balance < 0) return 'overdraft';
  if (balance === 0) return 'empty';
  return 'positive';
}
